import { DeleteOutlined, EditOutlined, UploadOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
	Button,
	Card,
	Col,
	Divider,
	Drawer,
	Form,
	Image,
	Input,
	message,
	Modal,
	Radio,
	Row,
	Space,
	Switch,
	Tabs,
	Tag,
	Tooltip,
	Typography,
	Upload,
	notification,
	Table,
	Select,
} from 'antd';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState, useEffect } from 'react';
import uploadApi from '../../api/file';
import menuApi from '../../api/menu';
import Constant from '../../lib/constant';
import { Tree } from 'antd';

const { Title, Text } = Typography;

// ====== IMPORTANT: KHÔNG tạo _id ======
const makeCid = () => `cid_${Date.now()}_${Math.random().toString(16).slice(2)}`;
const isObjectId = (v) => typeof v === 'string' && /^[a-fA-F0-9]{24}$/.test(v);
const nodeKey = (it) => it?._id || it?.__cid; // dùng cho Tree/UI

function cloneDeep(obj) {
	return JSON.parse(JSON.stringify(obj));
}

function getLabel(item) {
	return item?.names?.vi || item?.names?.en || '(no name)';
}

function normalizeLink(link) {
	const l = link && typeof link === 'object' ? link : {};
	const type = l.type || (l.pageId ? 'page' : 'url');
	return {
		type,
		href: type === 'url' ? l.href || '' : '',
		pageId: type === 'page' ? l.pageId || '' : '',
		openInNewTab: !!l.openInNewTab,
	};
}

function normalizeImage(image) {
	const img = image && typeof image === 'object' ? image : null;
	if (!img) return null;
	const url = img.url || '';
	const public_id = img.public_id || '';
	const signature = img.signature || '';
	if (!url && !public_id && !signature) return null;
	return { url, public_id, signature };
}

/**
 * ensureDefaults:
 * - GIỮ _id nếu là ObjectId hợp lệ
 * - Nếu _id không hợp lệ -> bỏ _id
 * - LUÔN có __cid để UI dùng
 */
function ensureDefaults(item) {
	const safe = item && typeof item === 'object' ? item : {};

	const _id = isObjectId(safe._id) ? safe._id : undefined;
	const __cid = safe.__cid || makeCid();

	const link = normalizeLink(safe.link);
	const image = normalizeImage(safe.image);

	return {
		...(_id ? { _id } : {}),
		__cid,

		enabled: safe.enabled ?? true,
		main: safe.main ?? false,
		names: safe.names || { vi: '', en: '' },
		descriptions: safe.descriptions || { vi: '', en: '' },
		link,
		image,
		order: typeof safe.order === 'number' ? safe.order : 0,
		code: safe.code ?? '',
		children: Array.isArray(safe.children) ? safe.children.map(ensureDefaults) : [],
	};
}

function normalizeOrder(list) {
	return (list || []).map((it, idx) => ({
		...it,
		order: idx,
		children: it.children?.length ? normalizeOrder(it.children) : [],
	}));
}

// ====== sanitize payload before save ======
// - remove __cid
// - remove invalid _id
// - recursively sanitize children
function sanitizeForSave(list) {
	const walk = (node) => {
		const out = { ...node };

		delete out.__cid;
		if (!isObjectId(out._id)) delete out._id;

		// link clean
		const link = out.link || {};
		if (link.type === 'page') {
			out.link = {
				type: 'page',
				pageId: link.pageId || '',
				openInNewTab: !!link.openInNewTab,
			};
		} else if (link.type === 'url') {
			out.link = {
				type: 'url',
				href: link.href || '',
				openInNewTab: !!link.openInNewTab,
			};
		} else {
			// nếu type trống thì vẫn gửi object rỗng cho backend tự xử lý
			out.link = { ...link };
		}

		// image clean
		out.image = normalizeImage(out.image);

		out.children = Array.isArray(out.children) ? out.children.map(walk) : [];
		return out;
	};

	return (list || []).map(walk);
}

// ---------- Tree Helpers (dùng nodeKey thay vì _id) ----------
function findAndUpdate(items, key, updater) {
	return items.map((it) => {
		if (nodeKey(it) === key) return updater(it);
		if (it.children?.length) return { ...it, children: findAndUpdate(it.children, key, updater) };
		return it;
	});
}

function findAndRemove(items, key) {
	const out = [];
	for (const it of items) {
		if (nodeKey(it) === key) continue;
		if (it.children?.length) out.push({ ...it, children: findAndRemove(it.children, key) });
		else out.push(it);
	}
	return out;
}

function findItem(items, key) {
	for (const it of items) {
		if (nodeKey(it) === key) return it;
		if (it.children?.length) {
			const found = findItem(it.children, key);
			if (found) return found;
		}
	}
	return null;
}

function addChild(items, parentKey, child) {
	return items.map((it) => {
		if (nodeKey(it) === parentKey) return { ...it, children: [...(it.children || []), child] };
		if (it.children?.length) return { ...it, children: addChild(it.children, parentKey, child) };
		return it;
	});
}

// ===== Drag & Drop Helpers (dùng nodeKey) =====
function findNodeWithParent(list, key, parent = null) {
	for (let i = 0; i < list.length; i++) {
		const node = list[i];
		if (nodeKey(node) === key) return { node, parent, index: i, siblings: list };
		if (node.children?.length) {
			const found = findNodeWithParent(node.children, key, node);
			if (found) return found;
		}
	}
	return null;
}

function removeNode(list, key) {
	const out = [];
	let removed = null;

	for (const it of list) {
		if (nodeKey(it) === key) {
			removed = it;
			continue;
		}
		if (it.children?.length) {
			const r = removeNode(it.children, key);
			if (r.removedNode) removed = r.removedNode;
			out.push({ ...it, children: r.newList });
		} else out.push(it);
	}

	return { newList: out, removedNode: removed };
}

function containsKey(node, targetKey) {
	if (!node) return false;
	if (nodeKey(node) === targetKey) return true;
	if (!node.children?.length) return false;
	return node.children.some((c) => containsKey(c, targetKey));
}

function renderPreview(items, level = 0) {
	const enabled = (items || []).filter((x) => x.enabled !== false);
	return enabled.map((it) => {
		const label = getLabel(it);
		const hasChildren = (it.children || []).some((c) => c.enabled !== false);
		const linkText =
			it?.link?.type === 'page' ? `page:${it.link.pageId || ''}` : it?.link?.href ? it.link.href : '';
		return (
			<div key={nodeKey(it)} style={{ paddingLeft: level * 14, paddingTop: 6, paddingBottom: 6 }}>
				<Space size={8} wrap>
					<Text>{label}</Text>
					{it.main ? <Tag color="gold">MAIN</Tag> : null}
					{it?.link?.type ? <Tag>{it.link.type}</Tag> : null}
					{linkText ? <Text type="secondary">({linkText})</Text> : null}
					{hasChildren ? <Text type="secondary">▾</Text> : null}
				</Space>
				{hasChildren ? <div style={{ marginTop: 6 }}>{renderPreview(it.children, level + 1)}</div> : null}
			</div>
		);
	});
}

function TV_menu_detail(props, ref) {
	const { getMenus, pages } = props;

	const [api, contextHolder] = notification.useNotification();
	const refMode = useRef('');

	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);

	const [menuKey, setMenuKey] = useState('');
	const [items, setItems] = useState([]);
	const [selectedKey, setSelectedKey] = useState(null);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [expandedKeys, setExpandedKeys] = useState([]);

	// CKEditor seed để remount editor khi đổi item
	const [editorSeed, setEditorSeed] = useState(0);
	const [editorInit, setEditorInit] = useState({ vi: '', en: '' });

	// Upload preview theo item đang sửa
	const [imageFileList, setImageFileList] = useState([]);

	const [exportOpen, setExportOpen] = useState(false);
	const [importOpen, setImportOpen] = useState(false);
	const [importText, setImportText] = useState('');

	const [form] = Form.useForm();

	// ===== Page Picker (UI kiểu cũ, filter client từ pages prop) =====
	const [pagePickerOpen, setPagePickerOpen] = useState(false);
	const [pagePickerPage, setPagePickerPage] = useState(1);
	const [pagePickerPageSize, setPagePickerPageSize] = useState(10);
	const [pagePickerFilters, setPagePickerFilters] = useState({
		search: '',
		template: undefined,
		enabled: undefined,
	});

	const selectedItem = useMemo(() => findItem(items, selectedKey), [items, selectedKey]);

	const openNotificationWithIcon = (type, msg, description) => {
		api[type]({ message: msg, description });
	};

	// ===== CKEditor upload adapter =====
	function uploadPlugin(editor) {
		editor.plugins.get('FileRepository').createUploadAdapter = (loader) => uploadAdapter(loader);
	}
	function uploadAdapter(loader) {
		return {
			upload: () =>
				new Promise(async (resolve, reject) => {
					try {
						const file = await loader.file;
						if (!file) return reject('No file');
						uploadApi
							.uploadImage(file)
							.then((data) => {
								resolve({ default: data?.data?.data?.url });
							})
							.catch(() => reject('Upload failed'));
					} catch (e) {
						reject('Error');
					}
				}),
			abort: () => {},
		};
	}

	const resetAll = () => {
		setMenuKey('');
		setItems([]);
		setSelectedKey(null);
		setDrawerOpen(false);
		setExpandedKeys([]);
		setExportOpen(false);
		setImportOpen(false);
		setImportText('');
		setEditorSeed((x) => x + 1);
		setEditorInit({ vi: '', en: '' });
		setImageFileList([]);
		form.resetFields();

		setPagePickerOpen(false);
		setPagePickerPage(1);
		setPagePickerPageSize(10);
		setPagePickerFilters({ search: '', template: undefined, enabled: undefined });
	};

	useImperativeHandle(ref, () => ({
		create: () => {
			resetAll();
			setOpen(true);
			refMode.current = Constant.crud.create;
		},
		update: async (record) => {
			try {
				resetAll();
				setLoading(true);

				const res = await menuApi.getByKeyAdmin(record.key);
				const menuDoc = res?.data?.data || {};
				const normalizedItems = normalizeOrder((menuDoc.items || []).map(ensureDefaults));

				setMenuKey(menuDoc.key || record.key || '');
				setItems(normalizedItems);

				setOpen(true);
				refMode.current = Constant.crud.update;
			} catch (e) {
				message.error(e?.message || 'Load menu failed');
			} finally {
				setLoading(false);
			}
		},
	}));

	const onCancel = () => {
		setOpen(false);
		refMode.current = '';
		resetAll();
	};

	// Build Tree Data for Antd Tree
	const toTreeData = (list) =>
		(list || []).map((it) => ({
			key: nodeKey(it),
			title: (
				<Space size={8}>
					<Text strong={!it.enabled} style={!it.enabled ? { opacity: 0.45 } : {}}>
						{getLabel(it)}
					</Text>

					{!it.enabled ? (
						<Tooltip title="Disabled">
							<span style={{ opacity: 0.5 }}>👁‍🗨</span>
						</Tooltip>
					) : (
						<Tooltip title="Enabled">
							<span style={{ opacity: 0.6 }}>👁</span>
						</Tooltip>
					)}

					{it.main ? (
						<Tooltip title="Main">
							<Tag color="gold" style={{ marginInlineStart: 4 }}>
								MAIN
							</Tag>
						</Tooltip>
					) : null}

					{it?.link?.type ? <Tag color="blue">{String(it.link.type).toUpperCase()}</Tag> : null}

					<Button
						className="border-0"
						style={{ background: 'none' }}
						onClick={(e) => {
							e.stopPropagation();
							openEditor(nodeKey(it));
						}}
						icon={<EditOutlined />}
					/>

					<Button
						className="border-0"
						danger
						style={{ background: 'none' }}
						onClick={(e) => {
							e.stopPropagation();
							deleteItem(nodeKey(it));
						}}
						icon={<DeleteOutlined />}
					/>
				</Space>
			),
			children: it.children?.length ? toTreeData(it.children) : undefined,
		}));

	const treeData = useMemo(() => toTreeData(items), [items]);

	const openEditor = (key) => {
		const it = findItem(items, key);
		if (!it) return;

		const normalized = ensureDefaults(it);

		setSelectedKey(key);
		setDrawerOpen(true);

		setEditorSeed((x) => x + 1);
		setEditorInit({
			vi: normalized.descriptions?.vi || '',
			en: normalized.descriptions?.en || '',
		});

		if (normalized.image?.url) {
			setImageFileList([{ uid: 'img-1', name: 'image', status: 'done', url: normalized.image.url }]);
		} else {
			setImageFileList([]);
		}

		form.setFieldsValue({
			enabled: normalized.enabled ?? true,
			main: normalized.main ?? false,
			order: normalized.order ?? 0,
			code: normalized.code ?? '',
			names_vi: normalized.names?.vi || '',
			names_en: normalized.names?.en || '',
			desc_vi: normalized.descriptions?.vi || '',
			desc_en: normalized.descriptions?.en || '',
			link_type: normalized.link?.type || 'url',
			link_href: normalized.link?.href || '',
			link_pageId: normalized.link?.pageId || '',
			link_newTab: normalized.link?.openInNewTab || false,
			link_pagePath: allPages.find((p) => p?._id === (normalized.link?.pageId || ''))?.path || '',
			image_url: normalized.image?.url || '',
			image_public_id: normalized.image?.public_id || '',
			image_signature: normalized.image?.signature || '',
		});
	};

	// ===== CREATE: KHÔNG set _id =====
	const addRootItem = () => {
		const newItem = ensureDefaults({
			__cid: makeCid(),
			names: { vi: 'Menu mới', en: 'New Menu' },
			descriptions: { vi: '', en: '' },
			link: { type: 'url', href: '', openInNewTab: false },
			children: [],
		});
		setItems((prev) => normalizeOrder([...(prev || []), newItem]));
		message.success('Đã thêm item cấp 1');
	};

	const addChildItem = (parentKey) => {
		const child = ensureDefaults({
			__cid: makeCid(),
			names: { vi: 'Menu con', en: 'Child' },
			descriptions: { vi: '', en: '' },
			link: { type: 'url', href: '', openInNewTab: false },
			children: [],
		});
		setItems((prev) => normalizeOrder(addChild(prev, parentKey, child)));
		setExpandedKeys((prev) => (prev.includes(parentKey) ? prev : [...prev, parentKey]));
		message.success('Đã thêm child');
	};

	const duplicateItem = (key) => {
		const it = findItem(items, key);
		if (!it) return;

		const cloned = cloneDeep(it);

		const renew = (node) => {
			delete node._id;
			node.__cid = makeCid(); // UI dùng
			if (node.children?.length) node.children.forEach(renew);
		};
		renew(cloned);

		setItems((prev) => normalizeOrder([...(prev || []), ensureDefaults(cloned)]));
		message.success('Đã duplicate (tạm add vào root)');
	};

	const deleteItem = (key) => {
		Modal.confirm({
			title: 'Xóa menu item?',
			content: 'Sẽ xóa luôn toàn bộ children bên trong.',
			okText: 'Xóa',
			okButtonProps: { danger: true },
			cancelText: 'Hủy',
			onOk: () => {
				setItems((prev) => normalizeOrder(findAndRemove(prev, key)));
				setExpandedKeys((prev) => prev.filter((k) => k !== key));
				if (selectedKey === key) {
					setSelectedKey(null);
					setDrawerOpen(false);
				}
				message.success('Đã xóa');
			},
		});
	};

	const toggleEnabled = (key) => {
		setItems((prev) =>
			normalizeOrder(
				findAndUpdate(prev, key, (it) => ({
					...it,
					enabled: !(it.enabled ?? true),
				}))
			)
		);
	};

	const onSaveItem = async () => {
		try {
			const v = await form.validateFields();

			const updated = (it) => {
				const link =
					v.link_type === 'page'
						? { type: 'page', pageId: v.link_pageId || '', openInNewTab: !!v.link_newTab }
						: { type: 'url', href: v.link_href || '', openInNewTab: !!v.link_newTab };

				const image =
					v.image_url || v.image_public_id || v.image_signature
						? {
								url: v.image_url || '',
								public_id: v.image_public_id || '',
								signature: v.image_signature || '',
							}
						: null;

				return ensureDefaults({
					...it,
					enabled: !!v.enabled,
					main: !!v.main,
					order: Number(v.order || 0),
					code: v.code || '',
					names: { ...(it.names || {}), vi: v.names_vi || '', en: v.names_en || '' },
					descriptions: { ...(it.descriptions || {}), vi: v.desc_vi || '', en: v.desc_en || '' },
					link,
					image,
				});
			};

			setItems((prev) => normalizeOrder(findAndUpdate(prev, selectedKey, updated)));
			message.success('Đã cập nhật item');
			setDrawerOpen(false);
		} catch (e) {
			// ignore
		}
	};

	// ===== SAVE: strip __cid và KHÔNG gửi _id lạ =====
	const mockUpsert = async () => {
		const key = (menuKey || '').trim();
		if (!key) return message.warning('Vui lòng nhập key menu');

		const payload = {
			key,
			items: sanitizeForSave(items),
		};

		setLoading(true);
		try {
			const res = await menuApi.update(payload);
			if (res) {
				openNotificationWithIcon('success', 'Thành công', 'Thao tác thành công');
				onCancel();
				getMenus?.();
			}
		} catch (e) {
			message.error(e?.message || 'Lưu thất bại');
		} finally {
			setLoading(false);
		}
	};

	// ====== DRAG & DROP HANDLER ======
	const handleDrop = (info) => {
		const dragKey = info.dragNode.key;
		const dropKey = info.node.key;

		const dropPosArr = info.node.pos.split('-');
		const dropIndexInPos = Number(dropPosArr[dropPosArr.length - 1]);
		const dropPosition = info.dropPosition - dropIndexInPos;

		const dragNode = findItem(items, dragKey);
		if (!dragNode) return;

		if (!info.dropToGap && containsKey(dragNode, dropKey)) {
			message.warning('Không thể thả vào chính node con của nó.');
			return;
		}

		const removedResult = removeNode(items, dragKey);
		const removedNode = removedResult.removedNode;
		if (!removedNode) return;

		let next = removedResult.newList;

		const dropFound = findNodeWithParent(next, dropKey);
		if (!dropFound) {
			next = normalizeOrder([...(next || []), removedNode]);
			setItems(next);
			return;
		}

		const { index: dropIdx, siblings: dropSiblings } = dropFound;

		if (!info.dropToGap) {
			const updated = (list) =>
				list.map((it) => {
					if (nodeKey(it) === dropKey) {
						const children = Array.isArray(it.children) ? it.children : [];
						return { ...it, children: [...children, removedNode] };
					}
					if (it.children?.length) return { ...it, children: updated(it.children) };
					return it;
				});

			next = updated(next);
			setExpandedKeys((prev) => (prev.includes(dropKey) ? prev : [...prev, dropKey]));
		} else {
			const insertAt = dropPosition < 0 ? dropIdx : dropIdx + 1;

			const oldDragFound = findNodeWithParent(items, dragKey);
			const oldDropFound = findNodeWithParent(items, dropKey);

			let finalInsertAt = insertAt;
			if (
				oldDragFound &&
				oldDropFound &&
				(nodeKey(oldDragFound.parent) || null) === (nodeKey(oldDropFound.parent) || null)
			) {
				if (oldDragFound.index < oldDropFound.index) finalInsertAt = insertAt - 1;
			}

			const boundedIndex = Math.max(0, Math.min(finalInsertAt, dropSiblings.length));
			dropSiblings.splice(boundedIndex, 0, removedNode);

			next = cloneDeep(next);
		}

		next = normalizeOrder(next);
		setItems(next);
		message.success('Đã cập nhật thứ tự menu');
	};

	// ===== EXPORT / IMPORT =====
	const exportJson = () => setExportOpen(true);
	const importJson = () => {
		setImportOpen(true);
		setImportText('');
	};
	const applyImport = () => {
		try {
			const obj = JSON.parse(importText);
			if (!obj || typeof obj !== 'object') throw new Error('Invalid JSON');
			if (!obj.key || !Array.isArray(obj.items)) throw new Error('Thiếu key hoặc items[]');

			setMenuKey(obj.key);
			// import cũng phải normalize: không ép tạo _id
			const normalized = normalizeOrder(obj.items.map(ensureDefaults));
			setItems(normalized);
			setImportOpen(false);
			message.success('Import thành công');
		} catch (e) {
			message.error(e?.message || 'Import lỗi');
		}
	};

	// ===== Upload ảnh cho item (bind vào form) =====
	const imageUploadProps = {
		accept: 'image/*',
		maxCount: 1,
		listType: 'picture-card',
		fileList: imageFileList,
		onChange: ({ fileList }) => {
			setImageFileList(fileList);
		},
		customRequest: async ({ file, onSuccess, onError }) => {
			try {
				const res = await uploadApi.uploadImage(file);
				const data = res?.data?.data || {};
				const url = data?.url || '';

				form.setFieldsValue({
					image_url: data?.url || '',
					image_public_id: data?.public_id || '',
					image_signature: data?.signature || '',
				});

				setImageFileList([{ uid: 'img-1', name: file?.name || 'image', status: 'done', url }]);

				message.success('Upload ảnh thành công');
				onSuccess?.(data);
			} catch (e) {
				message.error(e?.message || 'Upload ảnh lỗi');
				onError?.(e);
			}
		},
		onRemove: () => {
			form.setFieldsValue({
				image_url: '',
				image_public_id: '',
				image_signature: '',
			});
			setImageFileList([]);
			return true;
		},
	};

	// ===== Page Picker: dữ liệu lọc/paginate từ prop pages =====
	const allPages = useMemo(() => (Array.isArray(pages) ? pages : []), [pages]);

	useEffect(() => {
		setPagePickerPage(1);
	}, [pagePickerFilters.search, pagePickerFilters.template, pagePickerFilters.enabled]);

	const filteredPages = useMemo(() => {
		const s = (pagePickerFilters.search || '').trim().toLowerCase();
		return allPages.filter((p) => {
			if (pagePickerFilters.enabled !== undefined && pagePickerFilters.enabled !== null) {
				if (!!p.enabled !== !!pagePickerFilters.enabled) return false;
			}
			if (pagePickerFilters.template) {
				if ((p.template || '').toUpperCase() !== String(pagePickerFilters.template).toUpperCase()) return false;
			}
			if (!s) return true;

			const vi = (p?.names?.vi || '').toLowerCase();
			const en = (p?.names?.en || '').toLowerCase();
			const path = (p?.path || '').toLowerCase();
			const id = (p?._id || '').toLowerCase();
			return vi.includes(s) || en.includes(s) || path.includes(s) || id.includes(s);
		});
	}, [allPages, pagePickerFilters]);

	const pagePickerTotal = filteredPages.length;

	const pagedPages = useMemo(() => {
		const start = (pagePickerPage - 1) * pagePickerPageSize;
		return filteredPages.slice(start, start + pagePickerPageSize);
	}, [filteredPages, pagePickerPage, pagePickerPageSize]);

	const pickPage = (pageDoc) => {
		const id = pageDoc?._id;
		if (!id) return;

		form.setFieldsValue({
			link_type: 'page',
			link_pageId: id,
			link_pagePath: pageDoc?.path || '',
		});

		message.success(`Đã chọn: ${pageDoc?.path || id}`);
		setPagePickerOpen(false);
	};

	const pageColumns = [
		{
			title: 'Tên (VI)',
			dataIndex: ['names', 'vi'],
			key: 'vi',
			render: (v, r) => v || r?.names?.en || '(no name)',
		},
		{
			title: 'Path',
			dataIndex: 'path',
			key: 'path',
			render: (v) => <Text type="secondary">{v || '—'}</Text>,
		},
		{
			title: 'Template',
			dataIndex: 'template',
			key: 'template',
			render: (v) => (v ? <Tag>{v}</Tag> : <Tag>—</Tag>),
		},
		{
			title: 'Enabled',
			dataIndex: 'enabled',
			key: 'enabled',
			render: (v) => (v ? <Tag color="green">ON</Tag> : <Tag color="red">OFF</Tag>),
		},
		{
			title: 'Action',
			key: 'action',
			width: 110,
			render: (_, r) => (
				<Button type="primary" size="small" onClick={() => pickPage(r)}>
					Chọn
				</Button>
			),
		},
	];

	const resetPagePicker = () => {
		setPagePickerFilters({ search: '', template: undefined, enabled: undefined });
		setPagePickerPage(1);
		setPagePickerPageSize(10);
	};

	return (
		<Modal
			width="90%"
			open={open}
			onCancel={onCancel}
			footer={null}
			title={refMode.current === Constant.crud.create ? 'Tạo mới menu' : 'Cập nhật menu'}
		>
			{contextHolder}

			<div style={{ padding: 16 }}>
				<Row gutter={[16, 16]} align="middle">
					<Col flex="auto">
						<Title level={3} style={{ margin: 0 }}>
							Menu Builder
						</Title>
					</Col>
					<Col>
						<Space>
							<Button onClick={exportJson}>Export JSON</Button>
							<Button onClick={importJson}>Import JSON</Button>
							<Button type="primary" onClick={mockUpsert} loading={loading}>
								Lưu
							</Button>
						</Space>
					</Col>
				</Row>

				<Card style={{ marginTop: 12 }}>
					<Row gutter={12} align="middle">
						<Col xs={24} md={10}>
							<Space direction="vertical" style={{ width: '100%' }}>
								<Text strong>Key</Text>
								<Input
									value={menuKey}
									onChange={(e) => setMenuKey(e.target.value)}
									placeholder="main / footer / ..."
								/>
							</Space>
						</Col>
						<Col xs={24} md={14}>
							<Space wrap>
								<Button onClick={addRootItem}>Add Item (Root)</Button>
								{selectedKey ? (
									<>
										<Button onClick={() => addChildItem(selectedKey)}>+ Child</Button>
										<Button onClick={() => duplicateItem(selectedKey)}>Duplicate</Button>
										<Button
											danger
											icon={<DeleteOutlined />}
											onClick={() => deleteItem(selectedKey)}
										>
											Delete
										</Button>
										<Button onClick={() => toggleEnabled(selectedKey)}>
											Toggle Enabled {selectedItem?.enabled === false ? '(OFF)' : '(ON)'}
										</Button>
										<Button type="primary" onClick={() => openEditor(selectedKey)}>
											Sửa item đang chọn
										</Button>
									</>
								) : (
									<Text type="secondary">Chọn 1 item trong Tree để thao tác</Text>
								)}
							</Space>
						</Col>
					</Row>
				</Card>

				<Row gutter={[16, 16]} style={{ marginTop: 12 }}>
					<Col xs={24} lg={10}>
						<Card
							title="(A) Menu Items (Tree) — kéo thả để reorder / nest"
							extra={<Text type="secondary">Drag & Drop ON</Text>}
						>
							<Tree
								blockNode
								showLine
								draggable
								expandedKeys={expandedKeys}
								onExpand={(keys) => setExpandedKeys(keys)}
								selectedKeys={selectedKey ? [selectedKey] : []}
								treeData={treeData}
								onSelect={(keys) => {
									const key = keys?.[0];
									if (!key) return;
									openEditor(key);
								}}
								onDrop={handleDrop}
							/>
							<Divider />
							<Text type="secondary">
								Tip: Thả “đè” lên node để đưa vào <code>children</code>. Thả vào khoảng trống để reorder
								trước/sau.
							</Text>
						</Card>
					</Col>

					<Col xs={24} lg={14}>
						<Card title="(B) Live Preview">
							<div style={{ border: '1px solid #f0f0f0', borderRadius: 10, padding: 12 }}>
								<Space wrap>
									{(items || [])
										.filter((x) => x.enabled !== false)
										.map((it) => {
											const label = getLabel(it);
											const hasChildren = (it.children || []).some((c) => c.enabled !== false);
											return (
												<Tag
													key={nodeKey(it)}
													style={{ padding: '6px 10px', cursor: 'pointer' }}
													onClick={() => openEditor(nodeKey(it))}
												>
													{label} {hasChildren ? '▾' : ''}
												</Tag>
											);
										})}
								</Space>

								<Divider />
								<Text type="secondary">Hover dropdown (mock):</Text>
								<div style={{ marginTop: 8 }}>{renderPreview(items, 0)}</div>
							</div>
						</Card>
					</Col>
				</Row>

				{/* Drawer Editor */}
				<Drawer
					title={selectedItem ? `Sửa: ${getLabel(selectedItem)}` : 'Sửa Item'}
					open={drawerOpen}
					width="55%"
					onClose={() => setDrawerOpen(false)}
					footer={
						<Space style={{ float: 'right' }}>
							<Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
							<Button type="primary" onClick={onSaveItem}>
								Apply
							</Button>
						</Space>
					}
				>
					<Form
						form={form}
						layout="vertical"
						initialValues={{ enabled: true, link_type: 'url', link_newTab: false }}
					>
						<Row gutter={12}>
							<Col span={12}>
								<Form.Item name="enabled" label="Enabled" valuePropName="checked">
									<Switch />
								</Form.Item>
							</Col>
							<Col span={12}>
								<Form.Item name="main" label="Main (⭐)" valuePropName="checked">
									<Switch />
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={12}>
							<Col span={12}>
								<Form.Item name="order" label="Order">
									<Input type="number" />
								</Form.Item>
							</Col>
							<Col span={12}>
								<Form.Item name="code" label="Code (optional)">
									<Input placeholder="FE hook code" />
								</Form.Item>
							</Col>
						</Row>

						<Divider />

						<Tabs
							defaultActiveKey="vi"
							items={[
								{
									key: 'vi',
									label: 'VI',
									children: (
										<>
											<Form.Item
												name="names_vi"
												label="Name (VI)"
												rules={[{ required: true, message: 'Nhập name VI' }]}
											>
												<Input />
											</Form.Item>

											<Form.Item name="desc_vi" label="Description (VI)">
												<CKEditor
													key={`ck_vi_${selectedKey}_${editorSeed}`}
													editor={ClassicEditor}
													data={editorInit.vi || ''}
													config={{
														extraPlugins: [uploadPlugin],
														mediaEmbed: { previewsInData: true },
													}}
													onChange={(_, editor) => {
														const data = editor.getData();
														form.setFieldsValue({ desc_vi: data });
													}}
												/>
											</Form.Item>
										</>
									),
								},
								{
									key: 'en',
									label: 'EN',
									children: (
										<>
											<Form.Item name="names_en" label="Name (EN)">
												<Input />
											</Form.Item>

											<Form.Item name="desc_en" label="Description (EN)">
												<CKEditor
													key={`ck_en_${selectedKey}_${editorSeed}`}
													editor={ClassicEditor}
													data={editorInit.en || ''}
													config={{
														extraPlugins: [uploadPlugin],
														mediaEmbed: { previewsInData: true },
													}}
													onChange={(_, editor) => {
														const data = editor.getData();
														form.setFieldsValue({ desc_en: data });
													}}
												/>
											</Form.Item>
										</>
									),
								},
							]}
						/>

						<Divider />

						<Form.Item
							name="link_type"
							label="Link Type"
							rules={[{ required: true, message: 'Chọn link type' }]}
						>
							<Radio.Group>
								<Radio value="url">URL</Radio>
								<Radio value="page">PAGE</Radio>
							</Radio.Group>
						</Form.Item>

						<Form.Item shouldUpdate={(p, c) => p.link_type !== c.link_type} noStyle>
							{({ getFieldValue }) => {
								const type = getFieldValue('link_type');
								return (
									<>
										{type === 'url' ? (
											<Form.Item name="link_href" label="href">
												<Input placeholder="/contact hoặc https://..." />
											</Form.Item>
										) : (
											<>
												<Form.Item
													name="link_pageId"
													label="pageId"
													rules={[{ required: true, message: 'Chọn / nhập pageId' }]}
												>
													<Input
														placeholder="pageId..."
														onChange={(e) => {
															const id = e.target.value;
															const path =
																allPages.find((p) => p?._id === id)?.path || '';
															form.setFieldsValue({ link_pagePath: path });
														}}
														addonAfter={
															<Button type="link" onClick={() => setPagePickerOpen(true)}>
																Chọn từ danh sách
															</Button>
														}
													/>
												</Form.Item>

												<Form.Item name="link_pagePath" label="path (display only)">
													<Input disabled placeholder="/about-us/introduce ..." />
												</Form.Item>
											</>
										)}

										<Form.Item name="link_newTab" label="Open in new tab" valuePropName="checked">
											<Switch />
										</Form.Item>
									</>
								);
							}}
						</Form.Item>

						<Divider />

						<Text strong>Image (optional)</Text>
						<div style={{ marginTop: 8 }}>
							<Upload {...imageUploadProps}>
								{imageFileList?.length >= 1 ? null : (
									<div>
										<UploadOutlined />
										<div style={{ marginTop: 8 }}>Chọn ảnh</div>
									</div>
								)}
							</Upload>

							<Form.Item shouldUpdate noStyle>
								{() => {
									const url = form.getFieldValue('image_url');
									return url ? (
										<div style={{ marginTop: 10 }}>
											<Text type="secondary">Preview:</Text>
											<div style={{ marginTop: 8 }}>
												<Image src={url} width={240} />
											</div>
										</div>
									) : null;
								}}
							</Form.Item>

							<Row gutter={12} style={{ marginTop: 10 }}>
								<Col span={24}>
									<Form.Item name="image_url" label="image.url">
										<Input placeholder="https://..." />
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item name="image_public_id" label="image.public_id">
										<Input />
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item name="image_signature" label="image.signature">
										<Input />
									</Form.Item>
								</Col>
							</Row>
						</div>

						<Divider />

						<Space wrap>
							<Button onClick={() => selectedKey && addChildItem(selectedKey)}>+ Add Child</Button>
							<Button onClick={() => selectedKey && duplicateItem(selectedKey)}>Duplicate</Button>
							<Button
								danger
								icon={<DeleteOutlined />}
								onClick={() => selectedKey && deleteItem(selectedKey)}
							>
								Xóa
							</Button>
						</Space>
					</Form>
				</Drawer>

				{/* ===== Page Picker Modal (old-style UI) ===== */}
				<Modal
					title="Chọn Page"
					open={pagePickerOpen}
					onCancel={() => setPagePickerOpen(false)}
					footer={null}
					width={1100}
				>
					<Row gutter={[12, 12]} align="middle">
						<Col xs={24} md={10}>
							<Input
								allowClear
								prefix={<SearchOutlined />}
								placeholder="Tìm theo names.vi / names.en / path / _id"
								value={pagePickerFilters.search}
								onChange={(e) => setPagePickerFilters((p) => ({ ...p, search: e.target.value }))}
							/>
						</Col>

						<Col xs={24} md={6}>
							<Select
								style={{ width: '100%' }}
								allowClear
								placeholder="Template"
								value={pagePickerFilters.template}
								onChange={(v) => setPagePickerFilters((p) => ({ ...p, template: v }))}
								options={[
									{ value: 'SERVICE', label: 'SERVICE' },
									{ value: 'PROJECT', label: 'PROJECT' },
									{ value: 'NEWS_LIST', label: 'NEWS_LIST' },
									{ value: 'PRODUCT_LIST', label: 'PRODUCT_LIST' },
									{ value: 'PRODUCT_GROUP', label: 'PRODUCT_GROUP' },
								]}
							/>
						</Col>

						<Col xs={24} md={4}>
							<Select
								style={{ width: '100%' }}
								allowClear
								placeholder="Enabled"
								value={pagePickerFilters.enabled}
								onChange={(v) => setPagePickerFilters((p) => ({ ...p, enabled: v }))}
								options={[
									{ value: true, label: 'ON' },
									{ value: false, label: 'OFF' },
								]}
							/>
						</Col>

						<Col xs={24} md={4}>
							<Button icon={<ReloadOutlined />} block onClick={resetPagePicker}>
								Reset
							</Button>
						</Col>
					</Row>

					<Divider style={{ margin: '12px 0' }} />

					<Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
						<Col>
							<Text type="secondary">
								Kết quả: <b>{pagePickerTotal}</b>
							</Text>
						</Col>
					</Row>

					<Table
						rowKey="_id"
						columns={pageColumns}
						dataSource={pagedPages}
						pagination={{
							current: pagePickerPage,
							pageSize: pagePickerPageSize,
							total: pagePickerTotal,
							showSizeChanger: true,
							pageSizeOptions: [10, 20, 50, 100],
							onChange: (page, pageSize) => {
								setPagePickerPage(page);
								setPagePickerPageSize(pageSize);
							},
						}}
						size="middle"
					/>
				</Modal>

				{/* Export */}
				<Modal
					title="Export JSON"
					open={exportOpen}
					onCancel={() => setExportOpen(false)}
					footer={null}
					width={900}
				>
					<pre style={{ maxHeight: 520, overflow: 'auto', background: '#111', color: '#0f0', padding: 12 }}>
						{JSON.stringify({ key: (menuKey || '').trim(), items: sanitizeForSave(items) }, null, 2)}
					</pre>
				</Modal>

				{/* Import */}
				<Modal
					title="Import JSON"
					open={importOpen}
					onCancel={() => setImportOpen(false)}
					onOk={applyImport}
					okText="Apply"
					width={900}
				>
					<Input.TextArea
						value={importText}
						onChange={(e) => setImportText(e.target.value)}
						rows={18}
						placeholder={`Dán JSON dạng:\n{\n  "key":"main",\n  "items":[...]\n}`}
					/>
				</Modal>
			</div>
		</Modal>
	);
}

export default forwardRef(TV_menu_detail);
